import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Mail, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Color Palette (matching Login page)
  const colors = {
    primaryBlue: '#0082B0',
    primaryLight: '#0EA5E9',
    primaryDark: '#0284C7',
    background: '#0A1520',
    cardBg: '#0F1D2A',
    inputBg: '#0F172A',
    border: '#1E3A5F',
    text: '#E2E8F0',
    mutedText: '#94A3B8',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        toast.success("Check your email for reset instructions");
      } else {
        toast.error(data.detail || "Failed to send reset email");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{ 
          background: `linear-gradient(135deg, ${colors.background} 0%, #0D2137 50%, ${colors.background} 100%)`,
          fontFamily: "'DM Sans', sans-serif"
        }}
      >
        <div 
          className="w-full max-w-md p-8 rounded-2xl shadow-2xl text-center"
          style={{ 
            backgroundColor: colors.cardBg,
            border: `1px solid ${colors.border}`
          }}
        >
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}
          >
            <CheckCircle size={32} className="text-emerald-500" />
          </div>
          
          <h1 
            className="text-2xl font-bold mb-4"
            style={{ color: colors.text }}
          >
            Check Your Email
          </h1>
          
          <p 
            className="mb-6"
            style={{ color: colors.mutedText }}
          >
            If an account exists for <strong style={{ color: colors.text }}>{email}</strong>, 
            you'll receive a password reset link shortly.
          </p>
          
          <p 
            className="text-sm mb-8"
            style={{ color: colors.mutedText }}
          >
            The link will expire in 1 hour. Check your spam folder if you don't see it.
          </p>
          
          <Link to="/login">
            <Button
              className="w-full h-12 text-white"
              style={{ 
                background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{ 
        background: `linear-gradient(135deg, ${colors.background} 0%, #0D2137 50%, ${colors.background} 100%)`,
        fontFamily: "'DM Sans', sans-serif"
      }}
    >
      <div 
        className="w-full max-w-md p-8 rounded-2xl shadow-2xl"
        style={{ 
          backgroundColor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}
      >
        <Link 
          to="/login" 
          className="inline-flex items-center gap-2 mb-6 text-sm transition-colors hover:opacity-80"
          style={{ color: colors.primaryLight }}
        >
          <ArrowLeft size={16} />
          Back to Login
        </Link>

        <div className="text-center mb-8">
          <div 
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: 'rgba(14, 165, 233, 0.1)' }}
          >
            <Mail size={28} style={{ color: colors.primaryLight }} />
          </div>
          
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: colors.text }}
          >
            Forgot Password?
          </h1>
          
          <p style={{ color: colors.mutedText }}>
            Enter your email and we'll send you a link to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label 
              className="text-xs font-medium uppercase tracking-wider"
              style={{ color: colors.mutedText }}
            >
              Email Address
            </label>
            <Input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 px-4 border-0 text-base"
              style={{ 
                backgroundColor: colors.inputBg,
                color: colors.text
              }}
              data-testid="forgot-email-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full h-14 text-base font-medium text-white hover:opacity-90"
            style={{ 
              background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primaryDark} 100%)`
            }}
            disabled={loading}
            data-testid="forgot-submit-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="mr-2 h-5 w-5" />
                Send Reset Link
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
